"use client";

import React, { useCallback } from "react";
import { Check, Copy, Share } from "lucide-react";
import { useDeviceDetection } from "hooks/useDeviceDetection";
import { useToast } from "modules/common/components/toast/useToast";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { cn } from "utils/utils";
import { clientLogger } from "modules/common/logger/client";

interface ShareButtonProps {
  /** The URL to share */
  url: string;
  /** Optional title for the shared content */
  title?: string;
  /** Optional text/description for the shared content */
  text?: string;
  /** Custom button text (defaults to "Share") */
  buttonText?: string;
  /** Additional CSS classes */
  className?: string;
  /** Button size variant */
  size?: "sm" | "md" | "lg";
  /** Button style variant */
  variant?: "ghost" | "primary" | "secondary";
  /** If true, renders without default DaisyUI button classes - useful for custom styling */
  unstyled?: boolean;
  /** If true, only shows the icon without text */
  iconOnly?: boolean;
  /** Optional semantic role when the button is rendered inside a menu. */
  role?: React.AriaRole;
}

/**
 * ShareButton component that uses Web Share API when available,
 * falling back to clipboard copy with toast feedback
 */
export const ShareButton = ({
  url,
  title,
  text,
  buttonText = "Share",
  className,
  size = "sm",
  variant = "ghost",
  unstyled = false,
  iconOnly = false,
  role,
}: ShareButtonProps) => {
  const toast = useToast();
  const [isSharing, setIsSharing] = React.useState(false);
  const [justCopied, setJustCopied] = React.useState(false);

  // Get device info using the existing hook
  const { isMobile, isAndroid, isIOS } = useDeviceDetection();

  // Check if Web Share API is available
  const canWebShare = typeof navigator !== "undefined" && "share" in navigator;

  // Use native sharing only on mobile devices (phones/tablets with mobile OS)
  const shouldUseNativeSharing =
    canWebShare && isMobile && (isAndroid || isIOS);

  const handleShare = useCallback(async () => {
    if (isSharing) return;

    setIsSharing(true);

    try {
      // Ensure we have a valid absolute URL
      let shareUrl = url;
      if (!url.startsWith("http")) {
        // Handle relative URLs by getting current origin
        const currentOrigin = window.location.origin;
        shareUrl = url.startsWith("/")
          ? `${currentOrigin}${url}`
          : `${currentOrigin}/${url}`;
      }

      // Use native sharing only on mobile devices (Android/iOS)
      if (shouldUseNativeSharing) {
        const shareData = {
          url: shareUrl,
          ...(title && { title }),
          ...(text && { text }),
        };

        await navigator.share(shareData);
        // Note: navigator.share() resolves when user selects an app,
        // not when sharing is complete, so we don't show a success toast
        return;
      }

      // On desktop and non-mobile devices, always use clipboard
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);

        // Show success feedback
        setJustCopied(true);
        toast.success("Link copied to clipboard");

        // Reset the copied state after 2 seconds
        setTimeout(() => {
          setJustCopied(false);
        }, 2000);
      } else {
        // Final fallback for very old browsers
        // Create temporary text area for copying
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        setJustCopied(true);
        toast.success("Link copied to clipboard");

        setTimeout(() => {
          setJustCopied(false);
        }, 2000);
      }
    } catch (error) {
      // Handle errors (user cancelled sharing, permission denied, etc.)
      if (error instanceof Error && error.name !== "AbortError") {
        clientLogger.error("shareButton.share", "share failed", error);
        toast.error("Failed to share link");
      }
      // AbortError means user cancelled, which is normal behavior
    } finally {
      setIsSharing(false);
    }
  }, [url, title, text, shouldUseNativeSharing, isSharing, toast]);

  // Determine button classes - only apply if not unstyled
  const buttonClasses = unstyled
    ? className
    : cn(
        "btn",
        {
          "btn-sm": size === "sm",
          "btn-md": size === "md",
          "btn-lg": size === "lg",
          "btn-ghost": variant === "ghost",
          "btn-primary": variant === "primary",
          "btn-secondary": variant === "secondary",
        },
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      );

  // Choose appropriate icon based on platform and state
  const IconComponent = justCopied
    ? Check
    : shouldUseNativeSharing
      ? Share
      : Copy;
  const iconColor = justCopied ? "text-success" : "";

  return (
    <button
      type="button"
      role={role}
      onClick={handleShare}
      disabled={isSharing}
      aria-label={`Share ${title || "this page"}`}
      className={buttonClasses}
    >
      <IconComponent size={ICON_SIZE} className={iconColor} />
      {!iconOnly &&
        (justCopied
          ? "Copied!"
          : shouldUseNativeSharing
            ? buttonText
            : buttonText === "Share"
              ? "Copy Link"
              : buttonText)}
    </button>
  );
};
